import React, { useEffect, useState } from "react";
import { useWatch, useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import InputPhone from "@src/components/InputPhone";
import Button from "@src/components/Button";
import cashGift from "../cashGift.module.scss";
import toast, { LoaderIcon } from "react-hot-toast";
import {
  GenerateGiftPaymentMetadata,
  initiateStripePaymentIntent,
  VerifyTransaction,
} from "@src/graphql/mutations";
import {
  Currency,
  GiftPaymentMetadataRequest,
  EventSupportCurrencyProvider,
  UserQuery,
  UserQueryVariables,
  GenerateGiftPaymentMetadataMutation,
  GenerateGiftPaymentMetadataMutationVariables,
  InitiateStripePaymentIntentMutationVariables,
  InitiateStripePaymentIntentMutation,
  PaymentProcessor,
} from "@src/@types/api.d";
import { useMutation, useQuery } from "@apollo/client";
import { GetProfileDetails } from "@src/graphql/queries";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import getSymbolFromCurrency from "currency-symbol-map";
import useStore from "@src/store";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";
import { getPaymentErrorMessage } from "@src/utils/error";

interface Values {
  fullName: string;
  email: string;
  amount: string | number;
}

const GeneralFormSchema = Yup.object().shape({
  fullName: Yup.string().required("Full name is required"),
  email: Yup.string().email().required("Email is required"),
  amount: Yup.string().required("An amount is required"),
});

interface ForeignDetailsFormProps {
  eventId: number;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  eventUuid: string | undefined;
  currency: Currency | null;
  initialAmount?: number | string;
  itemName?: string;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies | null;
}

const ForeignDetailsForm = ({
  eventId,
  setOpen,
  eventUuid,
  currency,
  initialAmount,
  itemName,
  availableGiftPaymentCurrencies,
}: ForeignDetailsFormProps) => {
  const isPreviewMode = useStore((state) => state.isPreviewMode);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [formData, setFormData] = useState<Values | null>(null);

  // Find the wallet matching the current currency
  const matchingWallet = React.useMemo(() => {
    if (!availableGiftPaymentCurrencies || !currency) return null;
    return availableGiftPaymentCurrencies.find(
      (wallet) =>
        wallet &&
        wallet.currency === currency &&
        wallet.provider === EventSupportCurrencyProvider.Stripe
    );
  }, [availableGiftPaymentCurrencies, currency]);

  // Get stripe account ID if payment provider is stripe
  const stripeAccountId = React.useMemo(() => {
    if (
      matchingWallet &&
      matchingWallet.provider === EventSupportCurrencyProvider.Stripe &&
      matchingWallet.sub_account_code
    ) {
      return matchingWallet.sub_account_code;
    }
    return null;
  }, [matchingWallet]);

  // Create stripe promise dynamically with account ID when available
  const stripePromise = React.useMemo(() => {
    const stripeKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "";

    if (!stripeKey) {
      return null;
    }

    return stripeAccountId
      ? loadStripe(stripeKey, { stripeAccount: stripeAccountId })
      : loadStripe(stripeKey);
  }, [stripeAccountId]);

  const { data: profile } = useQuery<UserQuery, UserQueryVariables>(
    GetProfileDetails,
    {
      onError(error) {
        if (error.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        }
      },
    }
  );

  const [defaultValues, setDefaultValues] = useState<Values>({
    fullName: "",
    email: "",
    amount: initialAmount || "",
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm<Values>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(GeneralFormSchema),
  });

  useEffect(() => {
    if (profile?.user) {
      const newDefaultValues = {
        fullName: `${profile.user.first_name || ""} ${
          profile.user.last_name || ""
        }`.trim(),
        email: profile.user.email || "",
        amount: initialAmount || "",
      };
      setDefaultValues(newDefaultValues);

      reset(newDefaultValues, {
        keepDirty: !!initialAmount, // Keep dirty state if initialAmount is provided
      });

      // Trigger validation after reset
      setTimeout(() => {
        if (initialAmount) {
          setValue("amount", initialAmount, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      }, 0);
    }
  }, [profile, reset, initialAmount, setValue]);

  // const amount = useWatch({ control, name: "amount" });
  const [phoneNumberValue, setPhoneNumberValue] = React.useState<string>("");

  useEffect(() => {
    setPhoneNumberValue(profile?.user?.phone_number || "");
  }, [profile?.user?.phone_number]);

  // Mutation for generating payment metadata
  const [generateGiftMetadataFn] = useMutation<
    GenerateGiftPaymentMetadataMutation,
    GenerateGiftPaymentMetadataMutationVariables
  >(GenerateGiftPaymentMetadata);

  // Mutation for initiating Stripe payment
  const [initiateStripe] = useMutation<
    InitiateStripePaymentIntentMutation,
    InitiateStripePaymentIntentMutationVariables
  >(initiateStripePaymentIntent);

  const payWithStripe: SubmitHandler<Values> = async (data) => {
    if (!eventUuid) {
      toast.error("Event ID is missing");
      return;
    }

    setFormData(data);

    const input: GiftPaymentMetadataRequest = {
      amount: Number(data.amount),
      contact_email: data.email,
      contact_phone: phoneNumberValue,
      currency: currency as Currency,
      currency_iso: currency as Currency,
      event_id: eventUuid ?? "",
      full_name: data.fullName,
      note: "Cash gift payment",
      payment_provider: PaymentProcessor.Stripe,
    };

    try {
      const { data: mutationData } = await generateGiftMetadataFn({
        variables: {
          input,
        },
      });

      const paymentMeta = mutationData?.generateGiftPaymentMetadata;
      const paymentReference = paymentMeta?.payment_reference;

      if (!paymentReference) {
        toast.error(
          "We couldn't prepare your payment. Please try again in a moment."
        );
        return;
      }

      const { data: stripeData } = await initiateStripe({
        variables: {
          args: {
            customer_email: data.email,
            event_id: eventUuid,
            inawo_payment_reference: paymentReference,
          },
        },
      });

      const secret = stripeData?.initiateStripePaymentIntent?.client_secret;

      if (!secret) {
        toast.error(
          "We couldn't start your payment. Please try again in a moment."
        );
        return;
      }

      setClientSecret(secret);
    } catch (error) {
      console.error("Unable to start Stripe gift payment", error);
      toast.error(getPaymentErrorMessage(error));
    }
  };

  // console.log(availableGiftPaymentCurrencies);
  return !clientSecret ? (
    <div>
      <div className={cashGift["cashGift--content"]}>
        <div className={cashGift["cashGift--content__top"]}>
          <h1>
            {itemName || "Cash Gift"} {` (${currency?.toLocaleUpperCase()})`}{" "}
          </h1>
        </div>

        {isPreviewMode && (
          <p className="text-sm text-center text-[#71717A] mb-4">
            You are in preview mode.
          </p>
        )}

        <form className={cashGift["cashGift--content-form"]}>
          <div className={cashGift["cashGift--content-form_container"]}>
            <div className={cashGift["input"]}>
              <label htmlFor="fullName">Full Name</label>
              <input
                {...register("fullName")}
                type="text"
                name="fullName"
                placeholder="e.g Steve Bakery"
                className={cashGift["cashGift--content-form__field"]}
              />
            </div>
            <p className={cashGift["cashGift--content-form__errors"]}>
              {errors.fullName?.message} &nbsp;
            </p>
            <div className={cashGift["input"]}>
              <label htmlFor="email">Email</label>
              <input
                {...register("email")}
                type="text"
                className={cashGift["cashGift--content-form__field"]}
                name="email"
                placeholder="timi@gmail.com"
              />
            </div>
            <p className={cashGift["cashGift--content-form__errors"]}>
              {errors.email?.message}&nbsp;
            </p>
            <InputPhone
              name="phoneNumber"
              label="Phone Number"
              placeholder="Enter Phone Number (Whatsapp Number)"
              value={phoneNumberValue}
              setValue={setPhoneNumberValue}
            />
            <p className={cashGift["cashGift--content-form__errors"]}>&nbsp;</p>
            <div className={cashGift["input"]}>
              <label htmlFor="amount">Amount</label>
              <input
                {...register("amount")}
                type="number"
                name="amount"
                className={cashGift["cashGift--content-form__field"]}
                placeholder="Type the amount you want to gift"
              />
              {/* <div className={cashGift["cashGift--content-form__note"]}>
                <span>
                  {amount
                    ? paystackCharge?.with100
                      ? "+1.5% + ₦100 processing fee"
                      : "+1.5% processing fee"
                    : ""}
                </span>
              </div> */}
            </div>
            <p className={cashGift["cashGift--content-form__errors"]}>
              {errors.amount?.message} &nbsp;
            </p>
          </div>

          <div className={cashGift["cashGift--content-form_container__group"]}>
            <button
              type="button"
              onClick={() => {
                setValue("amount", 5, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={
                cashGift["cashGift--content-form_container__group--btn"]
              }
            >
              {getSymbolFromCurrency(currency as string)}5.00
            </button>
            <button
              type="button"
              onClick={() => {
                setValue("amount", 10, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={
                cashGift["cashGift--content-form_container__group--btn"]
              }
            >
              {getSymbolFromCurrency(currency as string)}10.00
            </button>
            <button
              type="button"
              onClick={() => {
                setValue("amount", 20, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={
                cashGift["cashGift--content-form_container__group--btn"]
              }
            >
              {getSymbolFromCurrency(currency as string)}20.00
            </button>
            <button
              type="button"
              onClick={() => {
                setValue("amount", 50, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={
                cashGift["cashGift--content-form_container__group--btn"]
              }
            >
              {getSymbolFromCurrency(currency as string)}50.00
            </button>
            <button
              type="button"
              onClick={() => {
                setValue("amount", 100, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={
                cashGift["cashGift--content-form_container__group--btn"]
              }
            >
              {getSymbolFromCurrency(currency as string)}100.00
            </button>
          </div>

          <div className={cashGift["cashGift--content-btnContainer"]}>
            <Button
              type="button"
              design="secondary"
              label="Pay via Paystack"
              // handleClick={handleSubmit(payWithPaystack)}
              handleClick={handleSubmit(payWithStripe)}
              disabled={
                phoneNumberValue === "" ||
                isSubmitting ||
                !isValid ||
                (!isDirty && !initialAmount)
              }
            >
              <div>
                <p className="">Pay via Stripe</p>
              </div>
            </Button>
          </div>
        </form>
      </div>
    </div>
  ) : (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#4e7e77",
            colorBackground: "#ffffff",
            colorText: "#1a1a1a",
            colorDanger: "#df1b41",
            fontFamily: "Inter, system-ui, sans-serif",
            spacingUnit: "4px",
            borderRadius: "8px",
            fontSizeBase: "14px",
          },
        },
      }}
    >
      <StripePaymentForm
        clientSecret={clientSecret}
        setOpen={setOpen}
        formData={formData}
        onGoBack={() => {
          setClientSecret(null);
          setFormData(null);
        }}
        currency={currency}
        itemName={itemName}
        // stripeAccountId={stripeAccountId}
      />
    </Elements>
  );
};

const StripePaymentForm = ({
  clientSecret,
  setOpen,
  formData,
  onGoBack,
  currency,
  itemName,
}: // stripeAccountId,
{
  clientSecret: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formData: Values | null;
  onGoBack: () => void;
  currency: Currency | null;
  itemName?: string;
  // stripeAccountId: string | null;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [paymentElementError, setPaymentElementError] = useState<string | null>(
    null
  );
  const loadTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Set timeout once on mount to detect if PaymentElement fails to load
  useEffect(() => {
    loadTimeoutRef.current = setTimeout(() => {
      // Set error - the timeout firing means element didn't load in time
      setPaymentElementError(
        "Payment form failed to load. The payment request may be invalid or expired. Please go back and try again."
      );
      loadTimeoutRef.current = null;
    }, 8000); // 8 second timeout

    // Cleanup on unmount
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Clear timeout when element becomes ready
  useEffect(() => {
    if (paymentElementReady && loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, [paymentElementReady]);

  // const [verifyTransactionFn] = useMutation<
  //   verifyTransaction,
  //   verifyTransactionVariables
  // >(VerifyTransaction, {
  //   onCompleted: (res) => {
  //     if (res?.verifyTransaction?.reference) {
  //       toast.success("Payment successful");
  //       setOpen(false);
  //     }
  //   },
  //   onError(error) {
  //     if (error.message !== "unauthenticated") {
  //       toast.error(
  //         <p className="toast">{error?.message ?? "An error occured"}</p>
  //       );
  //     }
  //   },
  // });

  const handleStripeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !formData) {
      return;
    }

    setIsProcessing(true);

    try {
      if (!stripe || !elements) {
        toast.error("Stripe is not ready yet. Please try again.");
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message || "Payment form validation failed");
        return;
      }

      const result = await stripe.confirmPayment({
        clientSecret,
        elements,
        confirmParams: {
          // return_url: `${window.location.origin}/payment/success`,
          return_url: `${window.location.origin}`,
          payment_method_data: {
            billing_details: {
              name: formData.fullName,
              email: formData.email,
              // address: {
              //   line1: formData.address,
              //   city: formData.city,
              //   postal_code: formData.zipCode,
              // },
            },
          },
        },
        redirect: "if_required",
      });

      if (result.error) {
        toast.error(result.error.message || "Payment failed");
        return;
      }
      if (result.paymentIntent.status === "succeeded") {
        // verifyTransactionFn({
        //   variables: {
        //     reference: result?.paymentIntent?.id,
        //     provider: paymentProcessor.stripe,
        //     subAccountId: stripeAccountId,
        //   },
        // });
        toast.success("Payment successful");
        setOpen(false);
        return;
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred during payment");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full sm:w-[551px] mx-auto bg-white rounded-md shadow-sm max-h-[90vh] flex flex-col overflow-hidden thin-scrollbar">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4e7e77] to-[#5a9189] px-6 py-5 rounded-t-md flex-shrink-0">
        <h2 className="text-white text-xl font-semibold font-inter mb-1">
          Complete Your Payment
        </h2>
        <p className="text-white/90 text-sm font-inter">
          Secure payment powered by Stripe
        </p>
      </div>

      {/* Payment Summary */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600 font-inter">Paying for:</span>
          <span className="text-base font-semibold text-gray-900 font-inter">
            {itemName || "Cash Gift"}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 font-inter">Amount:</span>
          <span className="text-lg font-bold text-[#4e7e77] font-inter">
            {getSymbolFromCurrency(currency || "")}
            {Number(formData?.amount || 0).toLocaleString()}
          </span>
        </div>
      </div>

      <form
        onSubmit={handleStripeSubmit}
        className="p-6 overflow-y-auto flex-1 thin-scrollbar"
      >
        {/* Error State */}
        {paymentElementError && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            {/* <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div> */}
            <p className="text-red-600 font-inter text-center mb-4 px-4">
              {paymentElementError}
            </p>
            <button
              type="button"
              onClick={onGoBack}
              className="text-sm py-2 px-6 bg-[#4e7e77] hover:bg-[#3d6660] text-white font-semibold font-inter rounded-lg transition duration-200"
            >
              Go Back
            </button>
          </div>
        )}

        {/* Loading State */}
        {!paymentElementReady && !paymentElementError && (
          <div className="flex flex-col items-center justify-center py-8 space-y-3">
            <LoaderIcon />
            <p className="text-sm text-gray-500 font-inter">
              Loading payment form...
            </p>
          </div>
        )}

        {/* Payment Element */}
        <div
          className={`${
            !paymentElementReady || paymentElementError ? "hidden" : ""
          }`}
        >
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3 font-inter">
              Card Details
            </label>
            <PaymentElement
              onLoaderStart={() => {
                setPaymentElementReady(false);
                // Don't clear error here - let timeout handle it
              }}
              onReady={() => {
                setPaymentElementReady(true);
                setPaymentElementError(null);
                // Clear timeout when ready
                if (loadTimeoutRef.current) {
                  clearTimeout(loadTimeoutRef.current);
                  loadTimeoutRef.current = null;
                }
              }}
              onChange={(e) => setIsPaymentComplete(e.complete)}
            />
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 py-4 mb-6 bg-gray-50 rounded-lg border border-gray-100">
            <svg
              className="w-5 h-5 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span className="text-sm text-gray-600 font-inter">
              Your payment is secure and encrypted
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              className="text-sm flex-1 py-2 px-3 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700 font-medium font-inter rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              onClick={onGoBack}
              disabled={isProcessing}
            >
              Go Back
            </button>
            <button
              type="submit"
              disabled={
                !stripe ||
                isProcessing ||
                !paymentElementReady ||
                !isPaymentComplete
              }
              className="text-sm flex-1 py-2 px-3 bg-[#4e7e77] hover:bg-[#3d6660] text-white font-semibold font-inter rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <LoaderIcon />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>
                    Pay {getSymbolFromCurrency(currency || "")}
                    {Number(formData?.amount || 0).toLocaleString()}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ForeignDetailsForm;
